import React from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { colorFor } from '../../../utils/itemPalette';
import { fpColors } from '../../../styles/theme';
import type { InventoryItemResponse } from '../../../api/types';

/**
 * Single-item card used by both the vertical `ItemRail` (list
 * below the fold) and the horizontal `FocusedItemCarousel` (single
 * card at the bottom of the first screen). Extracted from the old
 * ItemRail inline render so the two surfaces stay pixel-identical
 * — no style fork.
 *
 * Three interaction regions:
 *   - Left color tab  → toggle selection
 *   - Right check-pill→ toggle selection
 *   - Bottom chips    → refine / annotate / edit / delete
 *
 * Badges: AWAITING MASK (streaming, no mask yet), ANNOTATE
 * (mask_error), {N}% CONF (default), IoU pill (after refine).
 */

export interface ItemCardProps {
  item: InventoryItemResponse;
  selected: boolean;
  hasMask: boolean;
  maskErrored?: boolean;
  corrected?: boolean;
  iou?: number;
  /** Subtle glow for ~900 ms when the matching bbox was tapped. */
  highlighted?: boolean;
  /** Streaming flag drives the AWAITING MASK badge. */
  streaming?: boolean;
  /** Frosted-glass background for the carousel surface so the
   *  card doesn't merge with the dark photo behind it. Adds a
   *  backdrop-blur on web; native falls back to a solid surface. */
  glass?: boolean;
  onToggleSelected: (id: number) => void;
  onEditDetails: (id: number) => void;
  onRefineBbox: (id: number) => void;
  onDeleteItem: (id: number) => void;
  onAnnotate?: (id: number) => void;
  /** #169 C1 — read-only mode for the saved-scene viewer. Hides the
   *  scan-triage action chips (Refine / Annotate / Edit / Delete); the
   *  card stays tappable to SELECT (for Share). Default false keeps the
   *  live-scan behavior byte-for-byte unchanged. */
  readOnly?: boolean;
  /** Optional "Shared · {group}" badge text for read-only scene cards. */
  sharedLabel?: string;
  /** #170-followup — in read-only (scene-viewer) mode the whole card is
   *  tappable and opens the item's detail page. The top-right select
   *  circle is hidden in this mode (selection isn't used in the viewer). */
  onItemPress?: (item: InventoryItemResponse) => void;
}

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  selected,
  hasMask,
  maskErrored,
  corrected,
  iou,
  highlighted,
  streaming,
  glass = false,
  onToggleSelected,
  onEditDetails,
  onRefineBbox,
  onDeleteItem,
  onAnnotate,
  readOnly = false,
  sharedLabel,
  onItemPress,
}) => {
  const id = item.id ?? -1;
  const palette = colorFor(id);
  // In read-only (scene viewer) mode the entire card opens the item detail.
  // In live-scan mode it toggles selection (unchanged).
  const tappable = readOnly && !!onItemPress;
  const handlePress = () => {
    if (tappable) onItemPress?.(item);
    else if (id > 0) onToggleSelected(id);
  };
  const CardRoot: any = tappable ? Pressable : View;
  // #169 C1 — read-only scene cards surface present share-state derived
  // from item.groups[] (non-PERSONAL). Falls back to an explicit
  // sharedLabel override if provided.
  const sharedText: string | null = readOnly
    ? (() => {
        const gs = (
          Array.isArray((item as any).groups) ? (item as any).groups : []
        ).filter((g: any) => g && g.kind !== 'PERSONAL');
        if (gs.length === 0) return sharedLabel ?? null;
        return gs.length === 1
          ? `Shared · ${gs[0].name}`
          : `Shared · ${gs.length} circles`;
      })()
    : null;
  // Glass-morphism background for carousel cards that sit over
  // the photo. Always a semi-white frost so text reads cleanly;
  // selection state shows on the border + a subtle palette glow,
  // not the fill (which would muddy text legibility on a dark
  // photo backdrop).
  const glassBg = 'rgba(255,255,255,0.72)';
  const solidBg = selected
    ? palette.faint
    : fpColors.surfaceContainerLowest;
  return (
    <CardRoot
      onPress={tappable ? handlePress : undefined}
      accessibilityLabel={
        tappable ? `Open ${item.item_name || 'item'}` : undefined
      }
      style={[
        styles.card,
        {
          borderColor: selected ? palette.solid : fpColors.outlineVariant,
          backgroundColor: glass ? glassBg : solidBg,
        },
        glass && Platform.OS === 'web'
          ? ({
              backdropFilter: 'blur(22px) saturate(180%)',
              WebkitBackdropFilter: 'blur(22px) saturate(180%)',
              borderColor: selected
                ? palette.solid
                : 'rgba(255,255,255,0.45)',
              boxShadow:
                '0 8px 24px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.4)',
            } as any)
          : null,
        maskErrored && styles.cardMaskErrored,
        highlighted && {
          borderColor: palette.solid,
          ...(Platform.OS === 'web'
            ? ({
                boxShadow: `0 0 0 3px ${palette.solid}40, 0 6px 14px ${palette.solid}30`,
              } as any)
            : null),
        },
      ]}
      accessibilityRole="button"
      accessibilityState={{ selected }}
    >
      <Pressable
        onPress={handlePress}
        style={[styles.colorTab, { backgroundColor: palette.solid }]}
        accessibilityLabel={
          tappable ? 'Open item' : selected ? 'Deselect' : 'Select'
        }
        hitSlop={4}
      />
      <View style={styles.cardBody}>
        <View style={styles.topRow}>
          <Text style={styles.name} numberOfLines={1}>
            {item.item_name || 'Unnamed'}
          </Text>
          {hasMask ? (
            <MaterialIcons
              name="check-circle"
              size={12}
              color={palette.solid}
            />
          ) : null}
        </View>
        <Text style={styles.meta} numberOfLines={2}>
          {item.item_category || 'uncategorized'}
          {typeof item.estimated_value === 'number' &&
          item.estimated_value > 0
            ? ` · $${Math.round(item.estimated_value)}`
            : ''}
          {item.item_description ? ` · ${item.item_description}` : ''}
        </Text>
        <View style={styles.bottomRow}>
          {maskErrored ? (
            <View style={styles.errBadge}>
              <MaterialIcons name="warning" size={10} color="#92400E" />
              <Text style={styles.errBadgeText}>ANNOTATE</Text>
            </View>
          ) : !hasMask && streaming ? (
            <View style={styles.waitBadge}>
              <Text style={styles.waitBadgeText}>AWAITING MASK</Text>
            </View>
          ) : (
            <View
              style={[
                styles.confidenceBadge,
                { backgroundColor: palette.faint },
              ]}
            >
              <Text
                style={[styles.confidenceText, { color: palette.solid }]}
              >
                {formatConfidence(item.confidence_score)}
              </Text>
            </View>
          )}
          {corrected ? (
            <View style={styles.iouBadge}>
              <MaterialIcons
                name="verified"
                size={10}
                color={fpColors.primary}
              />
              <Text style={styles.iouBadgeText}>
                {typeof iou === 'number' && Number.isFinite(iou)
                  ? `IoU ${iou.toFixed(2)}`
                  : 'CORRECTED'}
              </Text>
            </View>
          ) : null}
          {sharedText ? (
            <View
              style={[
                styles.actionChip,
                {
                  backgroundColor: `${fpColors.primary}14`,
                  borderColor: `${fpColors.primary}55`,
                },
              ]}
              testID={`itemcard-shared-${id}`}
            >
              <MaterialIcons name="groups" size={12} color={fpColors.primary} />
              <Text style={[styles.actionChipText, { color: fpColors.primary }]}>
                {sharedText}
              </Text>
            </View>
          ) : null}
          {id > 0 && hasMask && !readOnly ? (
            <Pressable
              onPress={() => onRefineBbox(id)}
              hitSlop={6}
              style={styles.actionChip}
              accessibilityRole="button"
              accessibilityLabel="Refine bounding box"
            >
              <MaterialIcons
                name="crop-free"
                size={12}
                color={palette.solid}
              />
              <Text
                style={[styles.actionChipText, { color: palette.solid }]}
              >
                Refine box
              </Text>
            </Pressable>
          ) : null}
          {id > 0 && !hasMask && onAnnotate && !readOnly ? (
            <Pressable
              onPress={() => onAnnotate(id)}
              hitSlop={6}
              style={[
                styles.actionChip,
                {
                  backgroundColor: `${palette.solid}14`,
                  borderColor: `${palette.solid}55`,
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Annotate this item"
            >
              <MaterialIcons
                name="gesture"
                size={12}
                color={palette.solid}
              />
              <Text
                style={[styles.actionChipText, { color: palette.solid }]}
              >
                Annotate
              </Text>
            </Pressable>
          ) : null}
          {id > 0 && !readOnly ? (
            <Pressable
              onPress={() => onEditDetails(id)}
              hitSlop={6}
              style={styles.actionChip}
              accessibilityRole="button"
              accessibilityLabel="Edit details"
            >
              <MaterialIcons
                name="edit"
                size={12}
                color={fpColors.onSurface}
              />
              <Text style={styles.actionChipText}>Edit details</Text>
            </Pressable>
          ) : null}
          {id > 0 && !readOnly ? (
            <Pressable
              onPress={() => onDeleteItem(id)}
              hitSlop={6}
              style={[styles.actionChip, styles.actionChipDanger]}
              accessibilityRole="button"
              accessibilityLabel="Delete item"
            >
              <MaterialIcons
                name="delete-outline"
                size={12}
                color={fpColors.error}
              />
              <Text
                style={[styles.actionChipText, { color: fpColors.error }]}
              >
                Delete
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>
      {/* #170-followup — the select circle is only for the live-scan
          triage flow (pick which detected items to save). The read-only
          scene viewer no longer uses per-card selection, so it's hidden
          there and the whole card opens the item instead. */}
      {!readOnly ? (
        <Pressable
          onPress={() => id > 0 && onToggleSelected(id)}
          hitSlop={8}
          style={[
            styles.checkTap,
            {
              backgroundColor: selected ? palette.solid : 'transparent',
              borderColor: palette.solid,
              borderWidth: selected ? 0 : 2,
            },
          ]}
          accessibilityRole="button"
          accessibilityState={{ selected }}
          accessibilityLabel={selected ? 'Deselect' : 'Select'}
          // #169 testing — per-item select handle for the scan-accept toggle.
          testID={`itemcard-select-${id}`}
        >
          {selected ? (
            <MaterialIcons name="check" size={16} color={palette.onSolid} />
          ) : null}
        </Pressable>
      ) : null}
    </CardRoot>
  );
};

const formatConfidence = (c: number | undefined): string => {
  if (typeof c !== 'number' || !Number.isFinite(c)) return '—';
  return `${Math.round(c * 100)}% CONF`;
};

/**
 * Gap #123 follow-on (2026-05-27 audit) — the skeleton card used to
 * render as two faint horizontal lines on a near-transparent
 * background, which on the SegmentScreen "still scanning…" state
 * read as "an empty row, where did the item go?" rather than "a
 * placeholder for an incoming item." Add an explicit ✨ icon + label
 * "Looking for items…" so the intent is unambiguous while items
 * are still streaming.
 */
export const ItemCardSkeleton: React.FC = () => (
  <View style={[styles.card, styles.skelCard]}>
    <View style={[styles.colorTab, styles.skelTab]} />
    <View style={{ flex: 1, gap: 6, justifyContent: 'center' }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <Text style={{ fontSize: 14 }}>✨</Text>
        <Text
          style={{
            fontFamily: 'Inter_600SemiBold',
            fontSize: 13,
            color: fpColors.onSurface,
          }}
        >
          Looking for items…
        </Text>
      </View>
      <Text
        style={{
          fontFamily: 'Inter_400Regular',
          fontSize: 11,
          color: fpColors.onSurfaceVariant,
        }}
      >
        Usually 3–6 seconds.
      </Text>
    </View>
  </View>
);

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 10,
    paddingRight: 10,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 2,
    width: '100%',
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainerLowest,
    ...(Platform.OS === 'web'
      ? ({
          transition:
            'border-color 150ms ease-out, background-color 150ms ease-out',
        } as any)
      : null),
  },
  cardMaskErrored: {
    borderStyle: 'dashed',
    borderColor: '#D97706',
  },
  colorTab: {
    width: 6,
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
  },
  cardBody: { flex: 1, minWidth: 0, gap: 4 },
  topRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  name: {
    flex: 1,
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  meta: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    lineHeight: 16,
    color: fpColors.onSurfaceVariant,
  },
  bottomRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 4,
  },
  confidenceBadge: {
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
  },
  confidenceText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 9,
    letterSpacing: 0.5,
  },
  waitBadge: {
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  waitBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 9,
    letterSpacing: 0.5,
    color: fpColors.outline,
  },
  errBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: '#FEF3C7',
  },
  errBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 9,
    letterSpacing: 0.5,
    color: '#92400E',
  },
  iouBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    backgroundColor: `${fpColors.primary}14`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}40`,
  },
  iouBadgeText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 9,
    letterSpacing: 0.5,
    color: fpColors.primary,
  },
  actionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 5,
    borderRadius: 999,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  actionChipDanger: {
    backgroundColor: fpColors.errorContainer,
    borderColor: `${fpColors.error}40`,
  },
  actionChipText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 11,
    color: fpColors.onSurface,
  },
  checkTap: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
    // Pin to the top-right of the card so the bottom row of
    // action chips (Refine box / Edit details / Delete) can flow
    // freely without colliding with this big button.
    alignSelf: 'flex-start',
    marginTop: 2,
  },
  skelCard: {
    backgroundColor: fpColors.surfaceContainerLow,
    borderColor: fpColors.outlineVariant,
    borderStyle: 'solid',
  },
  skelTab: {
    backgroundColor: fpColors.outlineVariant,
  },
  skelLine: {
    height: 12,
    borderRadius: 4,
    backgroundColor: fpColors.outlineVariant,
  },
});
